import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { forgetMonthTerms, ordinal } from '../utils/months';

const styles = {
  h1: { fontSize: 22, fontWeight: 600, marginBottom: 20 },
  section: { backgroundColor: '#fff', borderRadius: 8, padding: 20, marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  sectionTitle: { fontSize: 16, fontWeight: 600, marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #eee' },
  label: { display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 4 },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 4, fontSize: 14, marginBottom: 12 },
  select: { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 4, fontSize: 14, marginBottom: 12 },
  textarea: { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 4, fontSize: 14, marginBottom: 12, height: 100, resize: 'vertical', fontFamily: 'monospace' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  row3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 },
  btn: { padding: '10px 20px', backgroundColor: '#4a9eff', color: '#fff', border: 'none', borderRadius: 4, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { padding: '10px 20px', backgroundColor: '#fff', color: '#333', border: '1px solid #ddd', borderRadius: 4, fontSize: 14, cursor: 'pointer' },
  btnRow: { display: 'flex', gap: 8, marginTop: 8 },
  message: { padding: '12px 16px', borderRadius: 6, fontSize: 14, marginBottom: 16 },
  success: { backgroundColor: '#e6f9e6', color: '#27ae60' },
  error: { backgroundColor: '#fee', color: '#e74c3c' },
  hint: { fontSize: 12, color: '#999', marginBottom: 12, marginTop: -8 },
  divider: { borderTop: '1px solid #eee', margin: '16px 0' },
  pwSection: { marginTop: 16, paddingTop: 16, borderTop: '1px solid #eee' },
};

// --- Template constants ---

const TEMPLATE_VARS = [
  { label: 'First Name', value: '{{first_name}}' },
  { label: 'Last Name', value: '{{last_name}}' },
  { label: 'Title Name', value: '{{title_name}}' },
  { label: 'End Date', value: '{{end_date}}' },
  { label: 'Company', value: '{{company_name}}' },
];

const HTML_TAGS = [
  { label: 'Bold', open: '<b>', close: '</b>' },
  { label: 'Italic', open: '<i>', close: '</i>' },
  { label: 'Underline', open: '<u>', close: '</u>' },
  { label: 'Paragraph', open: '<p>', close: '</p>' },
  { label: 'Line Break', value: '<br>' },
  { label: 'Heading', open: '<h3>', close: '</h3>' },
];

const EMAIL_STARTERS = {
  formal: {
    subject: 'Your {{title_name}} subscription is due for renewal',
    body: '<p>Dear {{first_name}},</p>\n<p>We are writing to inform you that your subscription to <b>{{title_name}}</b> is due to expire on <b>{{end_date}}</b>.</p>\n<p>To continue receiving your subscription without interruption, please renew at your earliest convenience.</p>\n<p>If you have already renewed, please disregard this notice.</p>\n<p>Kind regards,<br><b>{{company_name}}</b></p>',
  },
  friendly: {
    subject: "Don't miss out — renew your {{title_name}} subscription!",
    body: '<p>Hi {{first_name}},</p>\n<p>Just a quick note to let you know your <b>{{title_name}}</b> subscription expires on <b>{{end_date}}</b>.</p>\n<p>We\'d love to keep you as a reader! Renewing is easy — just get in touch and we\'ll sort it out for you.</p>\n<p>Thanks for your support,<br><b>{{company_name}}</b></p>',
  },
  short: {
    subject: '{{title_name}} subscription renewal',
    body: '<p>Dear {{first_name}},</p>\n<p>Your <b>{{title_name}}</b> subscription expires on <b>{{end_date}}</b>. Please contact us to renew.</p>\n<p>{{company_name}}</p>',
  },
};

const LETTER_STARTERS = {
  formal: '<p>Dear {{first_name}},</p>\n<p><b>Re: Your subscription to {{title_name}}</b></p>\n<p>We are writing to let you know that your subscription to {{title_name}} is due to expire on <b>{{end_date}}</b>.</p>\n<p>We hope you have enjoyed your subscription and would like to continue receiving it. To renew, please contact us at your earliest convenience.</p>\n<p>We look forward to hearing from you.</p>\n<p>Kind regards,<br><b>{{company_name}}</b></p>',
  friendly: '<p>Hi {{first_name}},</p>\n<p>We wanted to drop you a line — your <b>{{title_name}}</b> subscription is coming up for renewal on <b>{{end_date}}</b>.</p>\n<p>We really hope you\'ve been enjoying it and would love to have you continue as a subscriber. Just get in touch and we\'ll take care of everything.</p>\n<p>All the best,<br><b>{{company_name}}</b></p>',
  short: '<p>Dear {{first_name}},</p>\n<p>Your subscription to <b>{{title_name}}</b> expires on <b>{{end_date}}</b>. Please contact us to renew.</p>\n<p>{{company_name}}</p>',
};

// --- Pill bar component ---

function InsertBar({ textareaRef, settingKey, settings, setField, showFormatting }) {
  const insertAtCursor = (text) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const current = settings[settingKey] || '';
    const newVal = current.substring(0, start) + text + current.substring(end);
    setField(settingKey, newVal);
    setTimeout(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + text.length;
    }, 0);
  };

  const wrapSelection = (openTag, closeTag) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const current = settings[settingKey] || '';
    const selected = current.substring(start, end);
    const newVal = current.substring(0, start) + openTag + selected + closeTag + current.substring(end);
    setField(settingKey, newVal);
    setTimeout(() => {
      ta.focus();
      if (selected.length > 0) {
        ta.selectionStart = start + openTag.length;
        ta.selectionEnd = start + openTag.length + selected.length;
      } else {
        ta.selectionStart = ta.selectionEnd = start + openTag.length;
      }
    }, 0);
  };

  const pillBase = {
    padding: '3px 10px',
    fontSize: 12,
    borderRadius: 12,
    cursor: 'pointer',
    fontWeight: 500,
    border: 'none',
    transition: 'background 0.15s',
  };

  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center', marginBottom: showFormatting ? 5 : 0 }}>
        <span style={{ fontSize: 11, color: '#888', fontWeight: 600, marginRight: 2 }}>INSERT:</span>
        {TEMPLATE_VARS.map(v => (
          <button
            key={v.value}
            type="button"
            style={{ ...pillBase, background: '#e8f4fd', color: '#1a73a7' }}
            onClick={() => insertAtCursor(v.value)}
            title={`Insert ${v.value}`}
          >
            {v.label}
          </button>
        ))}
      </div>
      {showFormatting && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: '#888', fontWeight: 600, marginRight: 2 }}>FORMAT:</span>
          {HTML_TAGS.map(t => (
            <button
              key={t.label}
              type="button"
              style={{ ...pillBase, background: '#f0eef8', color: '#5b4a9e' }}
              onClick={() => t.value ? insertAtCursor(t.value) : wrapSelection(t.open, t.close)}
              title={t.value ? `Insert ${t.value}` : `Wrap with ${t.open}...${t.close}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Help modal ---

function TemplateHelpModal({ onClose }) {
  const overlay = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', zIndex: 9999,
  };
  const modal = {
    background: '#fff', borderRadius: 10, padding: '28px 32px', maxWidth: 620,
    width: '90%', maxHeight: '85vh', overflowY: 'auto', position: 'relative',
  };
  const heading = { fontSize: 18, fontWeight: 700, color: '#222', marginBottom: 16 };
  const subhead = { fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 8, marginTop: 20 };
  const para = { fontSize: 13, color: '#555', lineHeight: 1.6, marginBottom: 8 };
  const table = { width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 12 };
  const th = { textAlign: 'left', padding: '6px 10px', background: '#f5f6f8', fontWeight: 600, color: '#444', borderBottom: '1px solid #e0e0e0' };
  const td = { padding: '6px 10px', borderBottom: '1px solid #f0f0f0', color: '#555' };
  const code = { background: '#f0f4f8', padding: '1px 5px', borderRadius: 3, fontFamily: 'monospace', fontSize: 12, color: '#c7254e' };
  const exBox = { background: '#fafbfc', border: '1px solid #e8e8e8', borderRadius: 6, padding: '12px 16px', marginBottom: 12, fontSize: 13, lineHeight: 1.7 };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <button
          onClick={onClose}
          style={{ position: 'absolute', top: 12, right: 16, background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999' }}
        >&times;</button>

        <div style={heading}>How to use templates</div>

        <p style={para}>
          Templates let you write one message that is automatically personalised for each subscriber.
          You write placeholder names (called <b>variables</b>) inside double curly braces, and the system
          swaps them for the real values when sending.
        </p>

        <div style={subhead}>Variables</div>
        <p style={para}>Click any <b>INSERT</b> button above the text box, or type the variable name manually:</p>
        <table style={table}>
          <thead>
            <tr>
              <th style={th}>You type</th>
              <th style={th}>Gets replaced with</th>
              <th style={th}>Example result</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={td}><span style={code}>{'{{first_name}}'}</span></td><td style={td}>Subscriber's first name</td><td style={td}>Sarah</td></tr>
            <tr><td style={td}><span style={code}>{'{{last_name}}'}</span></td><td style={td}>Subscriber's surname</td><td style={td}>O'Brien</td></tr>
            <tr><td style={td}><span style={code}>{'{{title_name}}'}</span></td><td style={td}>Publication name</td><td style={td}>Irish Garden Magazine</td></tr>
            <tr><td style={td}><span style={code}>{'{{end_date}}'}</span></td><td style={td}>Subscription expiry date</td><td style={td}>1 March 2026</td></tr>
            <tr><td style={td}><span style={code}>{'{{company_name}}'}</span></td><td style={td}>Your company name (from Settings)</td><td style={td}>Garden Media Ltd</td></tr>
          </tbody>
        </table>

        <div style={subhead}>Example</div>
        <div style={exBox}>
          <div style={{ color: '#888', fontSize: 11, fontWeight: 600, marginBottom: 6 }}>TEMPLATE:</div>
          <div>Dear <span style={code}>{'{{first_name}}'}</span>, your subscription to <span style={code}>{'{{title_name}}'}</span> expires on <span style={code}>{'{{end_date}}'}</span>.</div>
          <div style={{ borderTop: '1px dashed #ddd', margin: '10px 0', paddingTop: 10 }}>
            <div style={{ color: '#888', fontSize: 11, fontWeight: 600, marginBottom: 6 }}>RESULT:</div>
            <div>Dear <b>Sarah</b>, your subscription to <b>Irish Garden Magazine</b> expires on <b>1 March 2026</b>.</div>
          </div>
        </div>

        <div style={subhead}>Formatting text</div>
        <p style={para}>
          Use the <b>FORMAT</b> buttons to add bold, italic, or other styling. You can also type the tags manually.
          Select some text first, then click a format button to wrap it — or click with nothing selected to insert empty tags.
        </p>
        <table style={table}>
          <thead>
            <tr>
              <th style={th}>You type</th>
              <th style={th}>What it does</th>
              <th style={th}>Looks like</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={td}><span style={code}>{'<b>text</b>'}</span></td><td style={td}>Bold</td><td style={td}><b>text</b></td></tr>
            <tr><td style={td}><span style={code}>{'<i>text</i>'}</span></td><td style={td}>Italic</td><td style={td}><i>text</i></td></tr>
            <tr><td style={td}><span style={code}>{'<u>text</u>'}</span></td><td style={td}>Underline</td><td style={td}><u>text</u></td></tr>
            <tr><td style={td}><span style={code}>{'<p>text</p>'}</span></td><td style={td}>New paragraph</td><td style={td}>Adds spacing between blocks</td></tr>
            <tr><td style={td}><span style={code}>{'<br>'}</span></td><td style={td}>Line break</td><td style={td}>Starts a new line</td></tr>
            <tr><td style={td}><span style={code}>{'<h3>text</h3>'}</span></td><td style={td}>Heading (larger text)</td><td style={td}><b style={{ fontSize: 15 }}>text</b></td></tr>
          </tbody>
        </table>

        <div style={subhead}>Tips</div>
        <ul style={{ ...para, paddingLeft: 20 }}>
          <li>Wrap each paragraph in <span style={code}>{'<p>...</p>'}</span> tags for proper spacing</li>
          <li>Use <span style={code}>{'<br>'}</span> for a line break within a paragraph (e.g. before a signature)</li>
          <li>You can combine tags: <span style={code}>{'<b><i>bold italic</i></b>'}</span></li>
          <li>Don't worry about being perfect — try a starter template and customise it</li>
        </ul>

        <div style={subhead}>Letters: what's added automatically</div>
        <p style={para}>
          For PDF letters, the system automatically adds your <b>company name and address</b> as a header,
          the <b>current date</b>, and the <b>subscriber's full postal address</b> above your letter body.
          You don't need to include these in your template.
        </p>

        <div style={{ textAlign: 'right', marginTop: 20 }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 24px', background: '#4a9eff', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >Got it</button>
        </div>
      </div>
    </div>
  );
}

// --- Label preview SVG ---

function LabelPreview({ preset, alignment, headerAlignment, hasLogo, customFormat, showReturnAddress, companyName, showTitleName, showSubNumber, showExpiry, subNumberPosition, addressFontSize, titleFontSize, extraFontSize, verticalAlignment, titleFirst }) {
  const subAtBottom = subNumberPosition === 'bottom';
  // Build header lines (pinned to top) and address lines (vertically aligned) separately
  const buildHeaderLines = () => {
    const titleLine = showTitleName ? { text: 'Test Title', style: 'title', align: headerAlignment || alignment } : null;
    const returnLine = showReturnAddress && companyName ? { text: companyName, style: 'return', align: headerAlignment || alignment } : null;
    const lines = [];
    if (titleFirst !== false) {
      if (titleLine) lines.push(titleLine);
      if (returnLine) lines.push(returnLine);
    } else {
      if (returnLine) lines.push(returnLine);
      if (titleLine) lines.push(titleLine);
    }
    return lines;
  };
  const buildAddressLines = () => {
    const lines = [];
    if (showSubNumber && !subAtBottom) {
      lines.push({ text: 'Sub #1001', style: 'extra', align: alignment });
    }
    lines.push({ text: 'Jane Smith', style: 'address', align: alignment });
    lines.push({ text: '42 Sample Street', style: 'address', align: alignment });
    lines.push({ text: 'Dublin', style: 'address', align: alignment });
    lines.push({ text: 'D02 XY45', style: 'address', align: alignment });
    if (showExpiry && !subAtBottom) {
      lines.push({ text: 'Exp: 25 May 2026', style: 'extra', align: alignment });
    }
    return lines;
  };
  // Combined for backwards compat
  const buildPreviewLines = () => [...buildHeaderLines(), ...buildAddressLines()];

  // If custom format with dimensions, show a proportional page grid
  if (customFormat) {
    const pageW = parseFloat(customFormat.pageWidth) || 210;
    const pageH = parseFloat(customFormat.pageHeight) || 297;
    const cols = parseInt(customFormat.columns) || 2;
    const rows = parseInt(customFormat.rows) || 7;
    const lw = parseFloat(customFormat.labelWidth) || 99.1;
    const lh = parseFloat(customFormat.labelHeight) || 38.1;
    const ml = parseFloat(customFormat.marginLeft) || 4.65;
    const mt = parseFloat(customFormat.marginTop) || 15.15;
    const gx = parseFloat(customFormat.gapX) || 2.5;
    const gy = parseFloat(customFormat.gapY) || 0;
    const padMm = parseFloat(customFormat.padding) || 3;

    // Scale to fit within SVG with margin so edge labels aren't clipped
    const margin = 4;
    const svgInner = 320;
    const scale = svgInner / pageW;
    const svgW = svgInner + margin * 2;
    const svgH = pageH * scale + margin * 2;

    const labels = [];
    const highlightIdx = cols > 0 ? cols - 1 : 0; // highlight top-right label
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = margin + (ml + c * (lw + gx)) * scale;
        const y = margin + (mt + r * (lh + gy)) * scale;
        const w = lw * scale;
        const h = lh * scale;
        const idx = r * cols + c;
        const isHighlight = idx === highlightIdx;
        labels.push({ x, y, w, h, isHighlight });
      }
    }

    const hlLabel = labels.find(l => l.isHighlight) || labels[0];
    const padScaled = padMm * scale;
    const innerX = hlLabel.x + padScaled;
    const innerY = hlLabel.y + padScaled;
    const innerW = hlLabel.w - padScaled * 2;
    const innerH = hlLabel.h - padScaled * 2;

    const previewLines = buildPreviewLines();
    // Use proportional font sizes scaled to fit the label cell
    const addrPt = addressFontSize || 9;
    const titlePt = titleFontSize || 11;
    const extraPt = extraFontSize || 7;
    const returnPt = 6.5;
    const getLinePt = (style) => {
      if (style === 'return') return returnPt;
      if (style === 'title') return titlePt;
      if (style === 'extra') return extraPt;
      return addrPt;
    };
    const totalPt = previewLines.reduce((s, l) => s + getLinePt(l.style) * 1.3, 0);
    const ptScale = totalPt > 0 ? Math.min(innerH / totalPt, 1) : 1;
    const maxFontPx = 8;

    return (
      <svg viewBox={`0 0 ${svgW} ${svgH}`} style={{ width: '100%', maxWidth: 328, border: '1px solid #ddd', borderRadius: 6, backgroundColor: '#fff' }}>
        {/* Page outline */}
        <rect x={margin} y={margin} width={svgInner} height={pageH * scale} fill="#fafafa" stroke="#ccc" strokeWidth={1} rx={2} />
        {/* All labels */}
        {labels.map((l, i) => (
          <rect key={i} x={l.x} y={l.y} width={l.w} height={l.h}
            fill={l.isHighlight ? '#e8f4fd' : '#fff'}
            stroke={l.isHighlight ? '#4a9eff' : '#ccc'}
            strokeWidth={l.isHighlight ? 1.5 : 0.5} rx={1} />
        ))}
        {/* Padding zone on highlighted label */}
        <rect x={innerX} y={innerY} width={innerW} height={innerH}
          fill="none" stroke="#4a9eff" strokeWidth={0.5} strokeDasharray="3,2" rx={1} />
        {/* Sample text in highlighted label */}
        {(() => {
          const headerLines = buildHeaderLines();
          const addrLines = buildAddressLines();
          const elements = [];
          let curY = innerY;

          // Render header lines (pinned to top)
          headerLines.forEach((line, i) => {
            const pt = getLinePt(line.style);
            const lh = pt * 1.3 * ptScale;
            const fs = Math.max(Math.min(pt * ptScale, maxFontPx), 3);
            curY += lh;
            if (curY > innerY + innerH + 1) return;
            const lineAlign = line.align || alignment;
            const tx = lineAlign === 'center' ? innerX + innerW / 2 : innerX + 2;
            const isReturn = line.style === 'return';
            const isTitle = line.style === 'title';
            elements.push(
              <text key={`h${i}`} x={tx} y={curY - lh * 0.15} textAnchor={lineAlign === 'center' ? 'middle' : 'start'}
                fontSize={fs} fontWeight={400}
                fill={isReturn ? '#999' : isTitle ? '#555' : '#666'}>
                {line.text}
              </text>
            );
          });

          // Calculate vertical offset for address block only
          const addrBlockH = addrLines.reduce((s, l) => s + getLinePt(l.style) * 1.3 * ptScale, 0);
          const hasBottomRow = subAtBottom && (showSubNumber || showExpiry);
          const bottomReserve = hasBottomRow ? Math.max(Math.min(extraPt * ptScale, maxFontPx), 3) + 2 : 0;
          const availH = (innerY + innerH) - curY - bottomReserve;
          let vOffset = 0;
          if (verticalAlignment === 'center') vOffset = Math.max(0, (availH - addrBlockH) / 2);
          else if (verticalAlignment === 'bottom') vOffset = Math.max(0, availH - addrBlockH);
          curY += vOffset;

          // Render address lines (vertically aligned)
          addrLines.forEach((line, i) => {
            const pt = getLinePt(line.style);
            const lh = pt * 1.3 * ptScale;
            const fs = Math.max(Math.min(pt * ptScale, maxFontPx), 3);
            curY += lh;
            if (curY > innerY + innerH + 1) return;
            const lineAlign = line.align || alignment;
            const tx = lineAlign === 'center' ? innerX + innerW / 2 : innerX + 2;
            const isExtra = line.style === 'extra';
            elements.push(
              <text key={`a${i}`} x={tx} y={curY - lh * 0.15} textAnchor={lineAlign === 'center' ? 'middle' : 'start'}
                fontSize={fs} fontWeight={400}
                fill={isExtra ? '#888' : '#666'}>
                {line.text}
              </text>
            );
          });

          // Bottom row when sub# at bottom
          if (subAtBottom) {
            const bfs = Math.max(Math.min(extraPt * ptScale, maxFontPx), 3);
            const by = innerY + innerH - 1;
            if (showSubNumber && showExpiry) {
              elements.push(<text key="bl" x={innerX + 2} y={by} textAnchor="start" fontSize={bfs} fill="#888">Sub #1001</text>);
              elements.push(<text key="br" x={innerX + innerW - 2} y={by} textAnchor="end" fontSize={bfs} fill="#888">Exp: 25 May 2026</text>);
            } else if (showSubNumber) {
              elements.push(<text key="bl" x={innerX + 2} y={by} textAnchor="start" fontSize={bfs} fill="#888">Sub #1001</text>);
            } else if (showExpiry) {
              elements.push(<text key="br" x={innerX + innerW - 2} y={by} textAnchor="end" fontSize={bfs} fill="#888">Exp: 25 May 2026</text>);
            }
          }
          return elements;
        })()}
        {/* Padding label */}
        <text x={innerX + innerW / 2} y={innerY + innerH + padScaled * 0.6}
          textAnchor="middle" fontSize={Math.max(padScaled * 0.7, 4)} fill="#4a9eff">
          {padMm}mm padding
        </text>
      </svg>
    );
  }

  // Standard Avery preview (single label with logo/alignment)
  const w = 300, h = 130;
  const pad = 10;
  const lw = w - pad * 2, lh = h - pad * 2;

  let logoRect = null;
  let textX = pad, textY = pad, textW = lw;

  const showLogo = hasLogo && preset !== 'standard';

  if (showLogo && preset === 'logo-left-strip') {
    const logoW = Math.round(lw * 0.28);
    logoRect = { x: pad, y: pad, w: logoW, h: lh };
    textX = pad + logoW + 8;
    textW = lw - logoW - 8;
  } else if (showLogo && preset === 'logo-top-left') {
    const logoH = Math.round(lh * 0.38);
    const logoW = Math.round(lw * 0.35);
    logoRect = { x: pad, y: pad, w: logoW, h: logoH };
    textY = pad + logoH + 6;
    textW = lw;
  } else if (showLogo && preset === 'logo-top-right') {
    const logoH = Math.round(lh * 0.38);
    const logoW = Math.round(lw * 0.35);
    logoRect = { x: pad + lw - logoW, y: pad, w: logoW, h: logoH };
    textY = pad + logoH + 6;
    textW = lw;
  }

  const previewLines = buildPreviewLines();
  // Scale font sizes proportionally — map pt sizes to SVG pixel sizes
  const addrPt = addressFontSize || 9;
  const titlePt = titleFontSize || 11;
  const extraPt = extraFontSize || 7;
  const returnPt = 6.5;
  const basePt = addrPt; // reference size
  const basePx = 10; // SVG pixel size for reference
  const pxPerPt = basePx / basePt;
  const getFs = (style) => {
    if (style === 'return') return Math.max(returnPt * pxPerPt, 6);
    if (style === 'title') return Math.min(titlePt * pxPerPt, 14);
    if (style === 'extra') return Math.max(extraPt * pxPerPt, 6);
    return basePx;
  };
  const getLh = (style) => getFs(style) * 1.3;

  const getAnchor = (a) => a === 'center' ? 'middle' : 'start';
  const getLineX = (a) => (a || alignment) === 'center' ? textX + textW / 2 : textX;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', maxWidth: 300, border: '1px solid #ddd', borderRadius: 6, backgroundColor: '#fff' }}>
      {/* Label outline */}
      <rect x={1} y={1} width={w - 2} height={h - 2} fill="none" stroke="#ccc" strokeWidth={1} rx={4} />
      {/* Logo placeholder */}
      {logoRect && (
        <g>
          <rect x={logoRect.x} y={logoRect.y} width={logoRect.w} height={logoRect.h} fill="#e8f0fe" stroke="#b0c4de" strokeWidth={1} rx={3} />
          <text x={logoRect.x + logoRect.w / 2} y={logoRect.y + logoRect.h / 2 + 4} textAnchor="middle" fontSize={11} fill="#5a7baa">LOGO</text>
        </g>
      )}
      {/* Text lines — mirrors actual PDF order: return addr + title pinned top, address vertically aligned */}
      {(() => {
        const headerLines = buildHeaderLines();
        const addrLines = buildAddressLines();
        const elements = [];
        let curY = textY;

        // Render header lines (pinned to top)
        headerLines.forEach((line, i) => {
          const isReturn = line.style === 'return';
          const isTitle = line.style === 'title';
          const lh = getLh(line.style);
          const fs = getFs(line.style);
          curY += lh;
          if (curY > h - pad) return;
          const lineAlign = line.align || alignment;
          elements.push(
            <text key={`h${i}`} x={getLineX(lineAlign)} y={curY} textAnchor={getAnchor(lineAlign)}
              fontSize={fs} fontWeight={400}
              fill={isReturn ? '#999' : isTitle ? '#555' : '#666'}>
              {line.text}
            </text>
          );
        });

        // Calculate vertical offset for address block only
        const addrBlockH = addrLines.reduce((s, l) => s + getLh(l.style), 0);
        const hasBottomRow = subAtBottom && (showSubNumber || showExpiry);
        const bottomReserve = hasBottomRow ? getFs('extra') + 4 : 0;
        const availH = (h - pad) - curY - bottomReserve;
        let vOffset = 0;
        if (verticalAlignment === 'center') vOffset = Math.max(0, (availH - addrBlockH) / 2);
        else if (verticalAlignment === 'bottom') vOffset = Math.max(0, availH - addrBlockH);
        curY += vOffset;

        // Render address lines (vertically aligned)
        addrLines.forEach((line, i) => {
          const isExtra = line.style === 'extra';
          const lh = getLh(line.style);
          const fs = getFs(line.style);
          curY += lh;
          if (curY > h - pad) return;
          const lineAlign = line.align || alignment;
          elements.push(
            <text key={`a${i}`} x={getLineX(lineAlign)} y={curY} textAnchor={getAnchor(lineAlign)}
              fontSize={fs} fontWeight={400}
              fill={isExtra ? '#888' : '#666'}>
              {line.text}
            </text>
          );
        });

        // Bottom row when sub# at bottom
        if (subAtBottom) {
          const bfs = getFs('extra');
          const by = h - pad - 2;
          if (showSubNumber && showExpiry) {
            elements.push(<text key="bl" x={textX} y={by} textAnchor="start" fontSize={bfs} fill="#888">Sub #1001</text>);
            elements.push(<text key="br" x={textX + textW} y={by} textAnchor="end" fontSize={bfs} fill="#888">Exp: 25 May 2026</text>);
          } else if (showSubNumber) {
            elements.push(<text key="bl" x={textX} y={by} textAnchor="start" fontSize={bfs} fill="#888">Sub #1001</text>);
          } else if (showExpiry) {
            elements.push(<text key="br" x={textX + textW} y={by} textAnchor="end" fontSize={bfs} fill="#888">Exp: 25 May 2026</text>);
          }
        }
        return elements;
      })()}
    </svg>
  );
}

// --- Main component ---

export default function Settings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [pwForm, setPwForm] = useState({ current: '', new: '', confirm: '' });
  const [showHelp, setShowHelp] = useState(false);

  const emailSubjectRef = useRef(null);
  const emailBodyRef = useRef(null);
  const letterBodyRef = useRef(null);

  useEffect(() => {
    api.getSettings().then(setSettings).finally(() => setLoading(false));
  }, []);

  const setField = (key, value) => setSettings(s => ({ ...s, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await api.updateSettings(settings);
      forgetMonthTerms(); // the other pages re-read "by the month"
      setMessage({ type: 'success', text: 'Settings saved' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to save' });
    }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    setMessage(null);
    if (!pwForm.current || !pwForm.new) {
      setMessage({ type: 'error', text: 'Enter current and new password' });
      return;
    }
    if (pwForm.new !== pwForm.confirm) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    try {
      await api.changePassword(pwForm.current, pwForm.new);
      setMessage({ type: 'success', text: 'Password changed' });
      setPwForm({ current: '', new: '', confirm: '' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to change password' });
    }
  };

  const loadEmailStarter = (key) => {
    if (!key) return;
    const t = EMAIL_STARTERS[key];
    if (!t) return;
    setField('renewal_email_subject', t.subject);
    setField('renewal_email_body', t.body);
  };

  const loadLetterStarter = (key) => {
    if (!key) return;
    const t = LETTER_STARTERS[key];
    if (!t) return;
    setField('renewal_letter_body', t);
  };

  if (loading) return <div>Loading...</div>;

  const starterSelect = {
    padding: '4px 8px', fontSize: 12, border: '1px solid #ddd', borderRadius: 4,
    background: '#fff', color: '#555', cursor: 'pointer',
  };

  return (
    <div>
      <h1 style={styles.h1}>Settings</h1>

      {message && (
        <div style={{ ...styles.message, ...(message.type === 'success' ? styles.success : styles.error) }}>
          {message.text}
        </div>
      )}

      <div style={styles.section}>
        <div style={styles.sectionTitle}>Company Details</div>
        <label style={styles.label}>Company Name</label>
        <input style={styles.input} value={settings.company_name || ''} onChange={e => setField('company_name', e.target.value)} />
        <label style={styles.label}>Company Address (used for return address and letterhead)</label>
        <textarea style={{ ...styles.textarea, height: 60 }} value={settings.company_address || ''} onChange={e => setField('company_address', e.target.value)} />
      </div>

      <div style={styles.section}>
        <div style={styles.sectionTitle}>Subscription Dates</div>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 14 }}>
          <input
            type="checkbox"
            checked={!!settings.subscription_cutoff_day}
            onChange={e => setField('subscription_cutoff_day', e.target.checked ? '28' : '')}
            data-testid="by-month"
          />
          <span>
            <b>Subscriptions run by the month</b> — dates are months (first issue December 2026,
            expiry November 2027), and a new subscription starts with the next issue the sign-up deadline allows.
          </span>
        </label>
        {!!settings.subscription_cutoff_day && (
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, flexWrap: 'wrap' }}>
            <span>Sign-up deadline: day</span>
            <select style={{ ...styles.select, width: 'auto', marginBottom: 0 }} value={settings.subscription_cutoff_day}
              onChange={e => setField('subscription_cutoff_day', e.target.value)} data-testid="cutoff-day">
              {Array.from({ length: 28 }, (_, i) => i + 1).map(n => <option key={n} value={String(n)}>{n}</option>)}
            </select>
            <span style={{ color: '#888', fontSize: 13 }}>
              of the month. Signed up by the {ordinal(+settings.subscription_cutoff_day)}: first issue next month; after it: the month after.
            </span>
          </div>
        )}
      </div>

      <div style={styles.section}>
        <div style={styles.sectionTitle}>Email Configuration (SMTP)</div>
        <div style={styles.row}>
          <div>
            <label style={styles.label}>SMTP Host</label>
            <input style={styles.input} value={settings.smtp_host || ''} onChange={e => setField('smtp_host', e.target.value)} placeholder="smtp.gmail.com" />
          </div>
          <div>
            <label style={styles.label}>SMTP Port</label>
            <input style={styles.input} value={settings.smtp_port || '587'} onChange={e => setField('smtp_port', e.target.value)} />
          </div>
        </div>
        <div style={styles.row}>
          <div>
            <label style={styles.label}>SMTP Username</label>
            <input style={styles.input} value={settings.smtp_user || ''} onChange={e => setField('smtp_user', e.target.value)} />
          </div>
          <div>
            <label style={styles.label}>SMTP Password</label>
            <input style={styles.input} type="password" value={settings.smtp_pass || ''} onChange={e => setField('smtp_pass', e.target.value)} />
          </div>
        </div>
        <div style={styles.row}>
          <div>
            <label style={styles.label}>From Email</label>
            <input style={styles.input} value={settings.smtp_from_email || ''} onChange={e => setField('smtp_from_email', e.target.value)} />
          </div>
          <div>
            <label style={styles.label}>From Name</label>
            <input style={styles.input} value={settings.smtp_from_name || ''} onChange={e => setField('smtp_from_name', e.target.value)} />
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, cursor: 'pointer', fontSize: 14 }}>
          <input
            type="checkbox"
            checked={settings.auto_send_renewals === 'true'}
            onChange={e => setField('auto_send_renewals', e.target.checked ? 'true' : 'false')}
          />
          <span>
            <b>Automatically email renewal reminders</b> — due reminders (per each title's
            reminder schedule) are emailed daily without needing to visit the Renewals page.
            Subscribers preferring letters are never auto-emailed; letters stay manual.
          </span>
        </label>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionTitle}>Label Format</div>
        <select style={styles.select} value={settings.label_format || 'L7163'} onChange={e => setField('label_format', e.target.value)}>
          <option value="L7163">Avery L7163 — 14 per sheet (99.1 x 38.1mm)</option>
          <option value="L7160">Avery L7160 — 21 per sheet (63.5 x 38.1mm)</option>
          <option value="L7162">Avery L7162 — 16 per sheet (99.1 x 33.9mm)</option>
          <option value="custom">Custom Label Size</option>
        </select>
        <div style={styles.sectionTitle}>Label Options</div>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={settings.label_show_subscriber_number === 'true'} onChange={e => setField('label_show_subscriber_number', e.target.checked ? 'true' : 'false')} />
            Show subscriber number
          </label>
          {settings.label_show_subscriber_number === 'true' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <label style={{ fontSize: 13, color: '#555' }}>Position:</label>
              <select style={{ ...styles.select, marginBottom: 0, width: 'auto' }} value={settings.label_sub_number_position || 'top'} onChange={e => setField('label_sub_number_position', e.target.value)}>
                <option value="top">Above address</option>
                <option value="bottom">Bottom row (with expiry)</option>
              </select>
            </div>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={settings.label_show_expiry_date === 'true'} onChange={e => setField('label_show_expiry_date', e.target.checked ? 'true' : 'false')} />
            Show expiry date
          </label>
          {settings.label_show_expiry_date === 'true' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={settings.label_show_expiry_line === 'true'} onChange={e => setField('label_show_expiry_line', e.target.checked ? 'true' : 'false')} />
              Line above expiry
            </label>
          )}
        </div>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={settings.label_show_title_name === 'true'} onChange={e => setField('label_show_title_name', e.target.checked ? 'true' : 'false')} />
            Show title name on labels
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={settings.label_show_return_address === 'true'} onChange={e => setField('label_show_return_address', e.target.checked ? 'true' : 'false')} />
            Show return address on labels
          </label>
          {settings.label_show_title_name === 'true' && settings.label_show_return_address === 'true' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={settings.label_title_first !== 'false'} onChange={e => setField('label_title_first', e.target.checked ? 'true' : 'false')} />
              Title name above return address
            </label>
          )}
        </div>
        {settings.label_show_title_name === 'true' && (
          <div style={{ ...styles.row, maxWidth: 420, marginBottom: 12 }}>
            <div>
              <label style={styles.label}>Title Font Size (pt)</label>
              <input style={styles.input} type="number" step="0.5" min="7" max="20" value={settings.label_title_font_size || '11'} onChange={e => setField('label_title_font_size', e.target.value)} />
            </div>
            <div>
              <label style={styles.label}>Title Colour</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="color" value={settings.label_title_color || '#000000'} onChange={e => setField('label_title_color', e.target.value)} style={{ width: 36, height: 32, border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer', padding: 2 }} />
                <input style={{ ...styles.input, marginBottom: 0, flex: 1 }} value={settings.label_title_color || '#000000'} onChange={e => setField('label_title_color', e.target.value)} placeholder="#000000" />
              </div>
            </div>
          </div>
        )}
        {settings.label_show_return_address === 'true' && (
          <div style={{ marginBottom: 12, padding: '10px 14px', backgroundColor: '#f8f9fa', borderRadius: 6, border: '1px solid #eee' }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Return address uses your Company Name and Address from General Settings above.</div>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, maxWidth: 900, marginBottom: 12 }}>
          <div>
            <label style={styles.label}>Layout Preset</label>
            <select style={styles.select} value={settings.label_layout_preset || 'standard'} onChange={e => setField('label_layout_preset', e.target.value)}>
              <option value="standard">Standard (no logo)</option>
              <option value="logo-top-left">Logo Top-Left</option>
              <option value="logo-top-right">Logo Top-Right</option>
              <option value="logo-left-strip">Logo Left Strip</option>
            </select>
          </div>
          <div>
            <label style={styles.label}>Title / Return Addr Alignment</label>
            <select style={styles.select} value={settings.label_header_alignment || 'left'} onChange={e => setField('label_header_alignment', e.target.value)}>
              <option value="left">Left</option>
              <option value="center">Centre</option>
            </select>
          </div>
          <div>
            <label style={styles.label}>Address Alignment</label>
            <select style={styles.select} value={settings.label_address_alignment || 'left'} onChange={e => setField('label_address_alignment', e.target.value)}>
              <option value="left">Left</option>
              <option value="center">Centre</option>
            </select>
          </div>
          <div>
            <label style={styles.label}>Vertical Alignment</label>
            <select style={styles.select} value={settings.label_vertical_alignment || 'top'} onChange={e => setField('label_vertical_alignment', e.target.value)}>
              <option value="top">Top</option>
              <option value="center">Centre</option>
              <option value="bottom">Bottom</option>
            </select>
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ ...styles.label, marginBottom: 6 }}>Preview</label>
          <LabelPreview
            preset={settings.label_layout_preset || 'standard'}
            alignment={settings.label_address_alignment || 'left'}
            headerAlignment={settings.label_header_alignment || 'left'}
            hasLogo={(settings.label_layout_preset || 'standard') !== 'standard'}
            showReturnAddress={settings.label_show_return_address === 'true'}
            companyName={settings.company_name}
            showTitleName={settings.label_show_title_name === 'true'}
            showSubNumber={settings.label_show_subscriber_number === 'true'}
            showExpiry={settings.label_show_expiry_date === 'true'}
            subNumberPosition={settings.label_sub_number_position || 'top'}
            addressFontSize={parseFloat(settings.label_font_size) || 9}
            titleFontSize={parseFloat(settings.label_title_font_size) || 11}
            extraFontSize={parseFloat(settings.label_extra_font_size) || 7}
            verticalAlignment={settings.label_vertical_alignment || 'top'}
            titleFirst={settings.label_title_first !== 'false'}
            customFormat={settings.label_format === 'custom' ? {
              pageWidth: settings.custom_label_page_width,
              pageHeight: settings.custom_label_page_height,
              columns: settings.custom_label_columns,
              rows: settings.custom_label_rows,
              labelWidth: settings.custom_label_width,
              labelHeight: settings.custom_label_height,
              marginLeft: settings.custom_label_margin_left,
              marginTop: settings.custom_label_margin_top,
              gapX: settings.custom_label_gap_x,
              gapY: settings.custom_label_gap_y,
              padding: settings.custom_label_padding,
            } : null}
          />
          {(settings.label_layout_preset || 'standard') !== 'standard' && settings.label_format !== 'custom' && (
            <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Upload a logo on each title's detail page for it to appear on labels.</div>
          )}
        </div>
        <div style={{ ...styles.row, maxWidth: 420, marginBottom: 12 }}>
          <div>
            <label style={styles.label}>Address Font Size (pt)</label>
            <input style={styles.input} type="number" step="0.5" min="5" max="16" value={settings.label_font_size || '9'} onChange={e => setField('label_font_size', e.target.value)} />
          </div>
          {(settings.label_show_subscriber_number === 'true' || settings.label_show_expiry_date === 'true') && (
            <div>
              <label style={styles.label}>Sub No. / Expiry Font Size (pt)</label>
              <input style={styles.input} type="number" step="0.5" min="5" max="16" value={settings.label_extra_font_size || '7'} onChange={e => setField('label_extra_font_size', e.target.value)} />
            </div>
          )}
        </div>
        {settings.label_format === 'custom' && (
          <div style={{ marginTop: 4 }}>
            <div style={styles.hint}>All dimensions in millimetres. Measure your label sheet carefully — include margins from the page edge to the first label.</div>
            <div style={styles.row}>
              <div>
                <label style={styles.label}>Label Width (mm)</label>
                <input style={styles.input} type="number" step="0.1" value={settings.custom_label_width || ''} onChange={e => setField('custom_label_width', e.target.value)} placeholder="99.1" />
              </div>
              <div>
                <label style={styles.label}>Label Height (mm)</label>
                <input style={styles.input} type="number" step="0.1" value={settings.custom_label_height || ''} onChange={e => setField('custom_label_height', e.target.value)} placeholder="38.1" />
              </div>
            </div>
            <div style={styles.row}>
              <div>
                <label style={styles.label}>Columns</label>
                <input style={styles.input} type="number" step="1" min="1" value={settings.custom_label_columns || ''} onChange={e => setField('custom_label_columns', e.target.value)} placeholder="2" />
              </div>
              <div>
                <label style={styles.label}>Rows</label>
                <input style={styles.input} type="number" step="1" min="1" value={settings.custom_label_rows || ''} onChange={e => setField('custom_label_rows', e.target.value)} placeholder="7" />
              </div>
            </div>
            <div style={styles.row}>
              <div>
                <label style={styles.label}>Left Margin (mm)</label>
                <input style={styles.input} type="number" step="0.1" value={settings.custom_label_margin_left || ''} onChange={e => setField('custom_label_margin_left', e.target.value)} placeholder="4.65" />
              </div>
              <div>
                <label style={styles.label}>Top Margin (mm)</label>
                <input style={styles.input} type="number" step="0.1" value={settings.custom_label_margin_top || ''} onChange={e => setField('custom_label_margin_top', e.target.value)} placeholder="15.15" />
              </div>
            </div>
            <div style={styles.row}>
              <div>
                <label style={styles.label}>Horizontal Gap (mm)</label>
                <input style={styles.input} type="number" step="0.1" value={settings.custom_label_gap_x || ''} onChange={e => setField('custom_label_gap_x', e.target.value)} placeholder="2.5" />
              </div>
              <div>
                <label style={styles.label}>Vertical Gap (mm)</label>
                <input style={styles.input} type="number" step="0.1" value={settings.custom_label_gap_y || ''} onChange={e => setField('custom_label_gap_y', e.target.value)} placeholder="0" />
              </div>
            </div>
            <div style={{ ...styles.row, maxWidth: 210 }}>
              <div>
                <label style={styles.label}>Internal Padding (mm)</label>
                <input style={styles.input} type="number" step="0.5" min="0" max="15" value={settings.custom_label_padding || '3'} onChange={e => setField('custom_label_padding', e.target.value)} placeholder="3" />
                <div style={styles.hint}>Space between label edge and text content</div>
              </div>
            </div>
            <div style={styles.row}>
              <div>
                <label style={styles.label}>Page Width (mm)</label>
                <input style={styles.input} type="number" step="0.1" value={settings.custom_label_page_width || ''} onChange={e => setField('custom_label_page_width', e.target.value)} placeholder="210" />
              </div>
              <div>
                <label style={styles.label}>Page Height (mm)</label>
                <input style={styles.input} type="number" step="0.1" value={settings.custom_label_page_height || ''} onChange={e => setField('custom_label_page_height', e.target.value)} placeholder="297" />
              </div>
            </div>
          </div>
        )}
        <div style={styles.divider} />
        <button
          type="button"
          style={{ ...styles.btnSecondary, fontSize: 13, marginBottom: 8 }}
          onClick={async () => {
            try {
              setMessage(null);
              await api.updateSettings(settings);
              await api.downloadTestLabel();
            } catch (err) {
              setMessage({ type: 'error', text: err.message || 'Failed to download test label' });
            }
          }}
        >Download Test Label</button>
        <div style={{ fontSize: 12, color: '#999' }}>Saves settings, then generates a single-label PDF with dummy data so you can print and check alignment</div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionTitle}>An Post International</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer', marginBottom: 16 }}>
          <input type="checkbox" checked={settings.anpost_enabled === 'true'} onChange={e => setField('anpost_enabled', e.target.checked ? 'true' : 'false')} />
          Enable An Post international CSV export
        </label>
        <div style={styles.hint}>When enabled, "Generate Labels" produces domestic (Ireland) labels only. A separate "An Post CSV" button appears for international subscribers.</div>
        {settings.anpost_enabled === 'true' && (
          <div>
            <div style={styles.divider} />
            <div style={styles.row}>
              <div>
                <label style={styles.label}>Account Number</label>
                <input style={styles.input} value={settings.anpost_account_number || ''} onChange={e => setField('anpost_account_number', e.target.value)} placeholder="Your An Post account number" />
              </div>
              <div>
                <label style={styles.label}>Service Type</label>
                <input style={styles.input} value={settings.anpost_service_type || 'Standard'} onChange={e => setField('anpost_service_type', e.target.value)} />
              </div>
            </div>
            <div style={styles.row3}>
              <div>
                <label style={styles.label}>Currency</label>
                <input style={styles.input} value={settings.anpost_currency || 'EUR'} onChange={e => setField('anpost_currency', e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Item Type</label>
                <input style={styles.input} value={settings.anpost_item_type || 'Retail'} onChange={e => setField('anpost_item_type', e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Description</label>
                <input style={styles.input} value={settings.anpost_description || 'Magazine'} onChange={e => setField('anpost_description', e.target.value)} />
              </div>
            </div>
            <div style={styles.row3}>
              <div>
                <label style={styles.label}>Origin Country</label>
                <input style={styles.input} value={settings.anpost_origin || 'Ireland'} onChange={e => setField('anpost_origin', e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Weight (kg)</label>
                <input style={styles.input} type="number" step="0.01" value={settings.anpost_weight || '0.11'} onChange={e => setField('anpost_weight', e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Declared Value</label>
                <input style={styles.input} type="number" step="0.01" value={settings.anpost_value || '2.50'} onChange={e => setField('anpost_value', e.target.value)} />
              </div>
            </div>

            <div style={{ ...styles.sectionTitle, marginTop: 8 }}>Postage Rates</div>
            <div style={styles.row}>
              <div>
                <label style={styles.label}>UK / Europe</label>
                <input style={styles.input} type="number" step="0.01" value={settings.anpost_rate_uk_europe || '3.50'} onChange={e => setField('anpost_rate_uk_europe', e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>USA</label>
                <input style={styles.input} type="number" step="0.01" value={settings.anpost_rate_usa || '4.32'} onChange={e => setField('anpost_rate_usa', e.target.value)} />
              </div>
            </div>
            <div style={styles.row}>
              <div>
                <label style={styles.label}>Canada</label>
                <input style={styles.input} type="number" step="0.01" value={settings.anpost_rate_canada || '4.55'} onChange={e => setField('anpost_rate_canada', e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Australia / Rest of World</label>
                <input style={styles.input} type="number" step="0.01" value={settings.anpost_rate_rest_of_world || '7.98'} onChange={e => setField('anpost_rate_rest_of_world', e.target.value)} />
              </div>
            </div>

            <div style={{ ...styles.sectionTitle, marginTop: 8 }}>HS Codes</div>
            <div style={styles.row}>
              <div>
                <label style={styles.label}>Default HS Code</label>
                <input style={styles.input} value={settings.anpost_hs_code_default || '4902902060'} onChange={e => setField('anpost_hs_code_default', e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>USA HS Code</label>
                <input style={styles.input} value={settings.anpost_hs_code_usa || '4902900000'} onChange={e => setField('anpost_hs_code_usa', e.target.value)} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* --- Email Template --- */}
      <div style={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #eee' }}>
          <span style={{ fontSize: 16, fontWeight: 600 }}>Renewal Email Template</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select style={starterSelect} onChange={e => { loadEmailStarter(e.target.value); e.target.value = ''; }} defaultValue="">
              <option value="" disabled>Load starter template...</option>
              <option value="formal">Formal</option>
              <option value="friendly">Friendly</option>
              <option value="short">Short</option>
            </select>
            <button
              type="button"
              onClick={() => setShowHelp(true)}
              style={{ padding: '4px 10px', fontSize: 12, border: '1px solid #4a9eff', borderRadius: 4, background: '#fff', color: '#4a9eff', cursor: 'pointer', fontWeight: 600 }}
            >? Help</button>
          </div>
        </div>

        <label style={styles.label}>Subject</label>
        <InsertBar textareaRef={emailSubjectRef} settingKey="renewal_email_subject" settings={settings} setField={setField} showFormatting={false} />
        <input ref={emailSubjectRef} style={styles.input} value={settings.renewal_email_subject || ''} onChange={e => setField('renewal_email_subject', e.target.value)} />

        <label style={styles.label}>Body (HTML supported)</label>
        <InsertBar textareaRef={emailBodyRef} settingKey="renewal_email_body" settings={settings} setField={setField} showFormatting={true} />
        <textarea ref={emailBodyRef} style={styles.textarea} value={settings.renewal_email_body || ''} onChange={e => setField('renewal_email_body', e.target.value)} />
      </div>

      {/* --- Letter Template --- */}
      <div style={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #eee' }}>
          <span style={{ fontSize: 16, fontWeight: 600 }}>Renewal Letter Template (PDF)</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select style={starterSelect} onChange={e => { loadLetterStarter(e.target.value); e.target.value = ''; }} defaultValue="">
              <option value="" disabled>Load starter template...</option>
              <option value="formal">Formal</option>
              <option value="friendly">Friendly</option>
              <option value="short">Short</option>
            </select>
            <button
              type="button"
              onClick={() => setShowHelp(true)}
              style={{ padding: '4px 10px', fontSize: 12, border: '1px solid #4a9eff', borderRadius: 4, background: '#fff', color: '#4a9eff', cursor: 'pointer', fontWeight: 600 }}
            >? Help</button>
          </div>
        </div>

        <label style={styles.label}>Letter Body (HTML supported — renders as formatted PDF)</label>
        <InsertBar textareaRef={letterBodyRef} settingKey="renewal_letter_body" settings={settings} setField={setField} showFormatting={true} />
        <textarea ref={letterBodyRef} style={{ ...styles.textarea, height: 160 }} value={settings.renewal_letter_body || ''} onChange={e => setField('renewal_letter_body', e.target.value)} />
        <div style={styles.hint}>Company header, date, and recipient address are added automatically above the letter body.</div>
      </div>

      <div style={styles.btnRow}>
        <button style={styles.btn} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      <div style={{ ...styles.section, marginTop: 24 }}>
        <div style={styles.sectionTitle}>Change Password</div>
        <label style={styles.label}>Current Password</label>
        <input style={styles.input} type="password" value={pwForm.current} onChange={e => setPwForm({ ...pwForm, current: e.target.value })} />
        <div style={styles.row}>
          <div>
            <label style={styles.label}>New Password</label>
            <input style={styles.input} type="password" value={pwForm.new} onChange={e => setPwForm({ ...pwForm, new: e.target.value })} />
          </div>
          <div>
            <label style={styles.label}>Confirm New Password</label>
            <input style={styles.input} type="password" value={pwForm.confirm} onChange={e => setPwForm({ ...pwForm, confirm: e.target.value })} />
          </div>
        </div>
        <button style={styles.btnSecondary} onClick={handleChangePassword}>Change Password</button>
      </div>

      {showHelp && <TemplateHelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
}
